import { comparePayloads, NodeId, TreeView, TreeViewNode } from '@fluid-experimental/tree';

import { logVerbose } from '../../log';
import { MfsValue } from '../..';
import { assertPrecondition, mfsError } from '../../errors';
import { isCollectionNode, isItemsRootNode } from './typeGuards';
import { MfsCollectionNode } from './types';

export function valueExistsInTraits<T>(
  view: TreeView,
  value: T,
  nodeIds: readonly NodeId[],
  areEqual: <T>(node: TreeViewNode, val: T) => boolean
): boolean {
  return nodeIds.some((nodeId) => {
    const node = view.getViewNode(nodeId);
    return areEqual<T>(node, value);
  });
}

export function findNodesWithPayloads(
  view: TreeView,
  payloads: MfsValue[],
  nodeIds: readonly NodeId[]
): readonly NodeId[] {
  return nodeIds.filter((nodeId) => {
    const node = view.getViewNode(nodeId);
    return payloads.some((p) => comparePayloads(node.payload, p));
  });
}

export function findParentCollection(view: TreeView, nodeId: NodeId): MfsCollectionNode | undefined {
  const parentNode = view.tryGetParentViewNode(nodeId);

  if (parentNode?.parentage?.parent === nodeId) {
    assertPrecondition(false, mfsError.circularReference);
  }

  if (!parentNode || isItemsRootNode(view, parentNode)) {
    logVerbose('findParentCollection', `Parent node undefined or node is root`, { nodeId });
    return undefined;
  }

  if (isCollectionNode(parentNode)) {
    return parentNode;
  }

  return findParentCollection(view, parentNode.identifier);
}

export function isAncestorOf(view: TreeView, ancestorNodeId: NodeId, descendantNodeId: NodeId): boolean {
  const parent = view.tryGetParentViewNode(descendantNodeId);

  if (!parent || isItemsRootNode(view, parent)) {
    logVerbose('isAncestorOf', `Parent node undefined or node is root`, { descendantNodeId });
    return false;
  }

  if (parent.identifier === ancestorNodeId) {
    return true;
  }

  return isAncestorOf(view, ancestorNodeId, parent.identifier);
}
